import { Component, Input, OnChanges } from '@angular/core';
import { NilSorting, SortDirection } from '@ddt-web/common';
import { PortfolioType } from '@ddt-web/config';
import { ColumnConfig, ColumnExtractor, ComponentData, TableData } from '@ddt-web/data-service';
import * as dayjs from 'dayjs';
import * as get from 'lodash/get';

@Component({
	selector: 'perf-common-period',
	templateUrl: './common-period.component.html',
	styleUrls: ['./common-period.component.scss']
})
export class CommonPeriodComponent implements OnChanges {
	@Input() public data: ComponentData;
	public tableDatas: TableData[];
	public portfolioTypes: string[];
	public sortedColumn: ColumnConfig<ColumnExtractor>;
	public sortDirection: SortDirection = SortDirection.DESC;
	public nilSorting: NilSorting = NilSorting.LARGEST;
	public commonPeriodStartDate: dayjs.Dayjs;
	public commonPeriodEndDate: dayjs.Dayjs;
	public driverPortfolioName: string;
	public driverSecurity: string;
	public isCommonPeriodLongerThanTenYears: boolean = false;
	public datesAreValid: boolean = false;
	public isModelPortfolio: boolean = false;

	public ngOnChanges(): void {
		if (this.data.success) {
			const tableDatas = get(this.data.getTabById('performance-common-period'), 'tableData', []);
			const portfolioTypeIndex = this.data.portfolioData[0].findIndex(column => column.config.id === 'portfolioType');
			this.portfolioTypes = this.data.portfolioData.map(data => data[portfolioTypeIndex].formattedValue);
			this.tableDatas = this.transformTableData(tableDatas);
			this.initCommonPeriod();
		}
	}

	public transformTableData(data: TableData[]): TableData[] {
		return data.map((tab: TableData, i: number) => {
			const titleIndex = tab.headersX.findIndex(header => header.config.id === 'description');
			this.sortedColumn = get(tab.headersX.find(header => header.config.id === 'startDate'), 'config', null);
			// In the case of a model portfolio, the start date is applicable to the entire portfolio rather than to any individual securities.
			if (this.portfolioTypes[i] === PortfolioType.MODEL || this.portfolioTypes[i] === PortfolioType.THIRD_PARTY) {
				// Here we set the cell content to be the name of the model - it would otherwise be empty
				tab.cells.forEach(cells => cells[titleIndex].formattedValue = tab.headersY[0].formattedValue);
			}
			return tab;
		});
	}

	private setCommonPeriodBounds(): void {
		const portfolioNames: string[] = this.tableDatas.map(tableData => tableData.headersY).reduce((acc, el) => acc.concat(el), []).map(cell => cell.formattedValue);

		const dateNameTypeTickerQuartets: DateNameTypeTickerQuartet[] = [];
		const endDates: number[] = [];

		this.tableDatas.reduce((acc, tableData, index) => { acc.push({ 'cells': tableData.cells, 'index': index }); return acc; }, []).forEach(cellsPerPortfolio => {
			const cells = cellsPerPortfolio.cells;
			const tableDataIndex = cellsPerPortfolio.index;
			const tableDataStartDates: number[] = cells.map(cell => cell.filter(cell => cell.config.id === 'startDate')).map(cell => cell[0].formattedValue ? dayjs(cell[0].formattedValue).valueOf() : null);
			const tableDataEndDates: number[] = cells.map(cell => cell.filter(cell => cell.config.id === 'endDate')).map(cell => cell[0].formattedValue ? dayjs(cell[0].formattedValue).valueOf() : null);

			const quartets: DateNameTypeTickerQuartet[] = tableDataStartDates.reduce((acc, startDate, cellIndex) => {
				const securityTicker = cells[cellIndex].find(cell => cell.config.id === 'ticker')?.formattedValue;
				const securityDescription = cells[cellIndex].find(cell => cell.config.id === 'description')?.formattedValue;
				const securityTickerOrDescription = securityTicker || securityDescription;
				acc.push({
					startDate,
					portfolioName: portfolioNames[tableDataIndex],
					portfolioType: this.portfolioTypes[tableDataIndex],
					driverSecurity: securityTickerOrDescription
				});
				return acc;
			}, []);

			dateNameTypeTickerQuartets.push(...quartets);
			endDates.push(...tableDataEndDates);
		});

		const sortedDateNameTypeTickerQuartets = [...dateNameTypeTickerQuartets].filter(quartet => !!quartet.startDate).sort((a, b) => b.startDate - a.startDate); // filter invalid dates and sort the start dates in descending order

		if (sortedDateNameTypeTickerQuartets.length) {
			const requiredQuartet = sortedDateNameTypeTickerQuartets[0]; // get the most recent start date
			this.commonPeriodStartDate = dayjs(requiredQuartet.startDate);
			this.driverPortfolioName = requiredQuartet.portfolioName;
			this.isModelPortfolio = requiredQuartet.portfolioType === PortfolioType.MODEL || requiredQuartet.portfolioType === PortfolioType.THIRD_PARTY;
			this.driverSecurity = requiredQuartet.driverSecurity;
		}
		const sortedEndDates = [...endDates].filter(num => !!num).sort((a, b) => a - b); // filter invalid dates and sort the end dates in ascending order		
		if (sortedEndDates.length) {
			this.commonPeriodEndDate = dayjs(sortedEndDates[0]); // get the earliest end date
		}
	}

	private initCommonPeriod(): void {
		this.setCommonPeriodBounds();
		this.datesAreValid = this.commonPeriodStartDate.isValid() && this.commonPeriodEndDate.isValid();
		if (this.datesAreValid) {
			this.isCommonPeriodLongerThanTenYears = this.commonPeriodEndDate.diff(this.commonPeriodStartDate, 'year') > 10;
		}
	}

}

interface DateNameTypeTickerQuartet {
	startDate: number;
	portfolioName: string;
	portfolioType: string;
	driverSecurity: string;
}
